'use client';

/**
 * ArchiMate Mapping Section
 * Allows viewing and creating mappings between Command Center entities
 * and ArchiMate diagram elements
 */

import { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '@/lib/archimate/WorkspaceContext';
import { getAllDiagrams, loadDiagram, type DiagramMetadata } from '@/lib/archimate/storage/db';
import type { ArchiMateElement } from '@/lib/archimate/types';
import type { ArchiMateMapping, MappingType, SyncDirection } from '@/lib/command-center/types';

interface ArchiMateMappingSectionProps {
  entityId: string;
  entityName: string;
}

// Mapping type labels
const MAPPING_TYPE_LABELS: Record<MappingType, string> = {
  exact: 'Exact Match',
  represents: 'Represents',
  contains: 'Contains',
};

// Sync direction labels
const SYNC_DIRECTION_LABELS: Record<SyncDirection, string> = {
  bidirectional: 'Bidirectional',
  to_archimate: 'To ArchiMate',
  from_archimate: 'From ArchiMate',
};

export default function ArchiMateMappingSection({ entityId, entityName }: ArchiMateMappingSectionProps) {
  const { currentWorkspace, loading: workspaceLoading } = useWorkspace();

  // State for existing mappings
  const [mappings, setMappings] = useState<ArchiMateMapping[]>([]);
  const [loadingMappings, setLoadingMappings] = useState(true);

  // State for creating new mapping
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [diagrams, setDiagrams] = useState<DiagramMetadata[]>([]);
  const [selectedDiagramId, setSelectedDiagramId] = useState<string>('');
  const [elements, setElements] = useState<ArchiMateElement[]>([]);
  const [selectedElementId, setSelectedElementId] = useState<string>('');
  const [mappingType, setMappingType] = useState<MappingType>('exact');
  const [syncDirection, setSyncDirection] = useState<SyncDirection>('bidirectional');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch existing mappings
  const fetchMappings = useCallback(async () => {
    try {
      const response = await fetch(`/api/secure/command-center/archimate-mappings?entity_id=${entityId}`);
      if (response.ok) {
        const data = await response.json();
        setMappings(data.mappings || []);
      }
    } catch (err) {
      console.error('Error fetching mappings:', err);
    } finally {
      setLoadingMappings(false);
    }
  }, [entityId]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  // Load diagrams when workspace is available
  useEffect(() => {
    async function loadDiagrams() {
      if (!currentWorkspace) return;

      try {
        const diagramList = await getAllDiagrams(currentWorkspace.id);
        setDiagrams(diagramList);
      } catch (err) {
        console.error('Error loading diagrams:', err);
      }
    }

    loadDiagrams();
  }, [currentWorkspace]);

  // Load elements when diagram is selected
  useEffect(() => {
    async function loadElements() {
      if (!selectedDiagramId) {
        setElements([]);
        return;
      }

      try {
        const diagram = await loadDiagram(selectedDiagramId);
        if (diagram) {
          setElements(diagram.elements || []);
        }
      } catch (err) {
        console.error('Error loading elements:', err);
        setElements([]);
      }
    }

    loadElements();
  }, [selectedDiagramId]);

  // Create new mapping
  const handleCreateMapping = async () => {
    if (!currentWorkspace || !selectedDiagramId || !selectedElementId) {
      setError('Please select a diagram and element');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const response = await fetch('/api/secure/command-center/archimate-mappings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entity_id: entityId,
          archimate_workspace_id: currentWorkspace.id,
          archimate_diagram_id: selectedDiagramId,
          archimate_element_id: selectedElementId,
          mapping_type: mappingType,
          sync_direction: syncDirection,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create mapping');
      }

      // Reset form and refresh mappings
      setShowCreateForm(false);
      setSelectedDiagramId('');
      setSelectedElementId('');
      setMappingType('exact');
      setSyncDirection('bidirectional');
      await fetchMappings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create mapping');
    } finally {
      setCreating(false);
    }
  };

  // Delete mapping
  const handleDeleteMapping = async (mappingId: string) => {
    if (!confirm('Are you sure you want to remove this mapping?')) return;

    try {
      const response = await fetch(`/api/secure/command-center/archimate-mappings/${mappingId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await fetchMappings();
      }
    } catch (err) {
      console.error('Error deleting mapping:', err);
    }
  };

  if (workspaceLoading) {
    return (
      <div className="py-3">
        <p className="text-foreground-muted text-sm">Loading ArchiMate workspace...</p>
      </div>
    );
  }

  if (!currentWorkspace) {
    return (
      <div className="py-3">
        <p className="text-foreground-muted text-sm">No ArchiMate workspace available</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm text-foreground-tertiary">ArchiMate Mappings</label>
        {!showCreateForm && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="text-accent text-sm hover:text-accent transition-colors"
          >
            + Add Mapping
          </button>
        )}
      </div>

      {/* Existing mappings */}
      {loadingMappings ? (
        <p className="text-foreground-muted text-sm">Loading mappings...</p>
      ) : mappings.length === 0 && !showCreateForm ? (
        <p className="text-foreground-muted text-sm">No ArchiMate mappings yet</p>
      ) : (
        <div className="space-y-2">
          {mappings.map((mapping) => (
            <div
              key={mapping.id}
              className="bg-background/50 rounded-lg p-3 border border-border-muted"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-foreground text-sm font-medium truncate">
                    Element: {mapping.archimate_element_id}
                  </p>
                  <p className="text-foreground-tertiary text-xs mt-1">
                    {MAPPING_TYPE_LABELS[mapping.mapping_type]} &bull; {SYNC_DIRECTION_LABELS[mapping.sync_direction]}
                  </p>
                  <p className="text-foreground-muted text-xs mt-1">
                    Diagram: {mapping.archimate_diagram_id || 'Unknown'}
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteMapping(mapping.id)}
                  className="text-red-400 hover:text-red-300 p-1"
                  title="Remove mapping"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create mapping form */}
      {showCreateForm && (
        <div className="bg-background/50 rounded-lg p-4 border border-border-muted space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-foreground font-medium">Map to ArchiMate Element</h4>
            <button
              onClick={() => setShowCreateForm(false)}
              className="text-foreground-muted hover:text-foreground"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <p className="text-red-400 text-sm">{error}</p>
          )}

          {/* Diagram selector */}
          <div>
            <label className="block text-sm text-foreground-tertiary mb-1">Diagram</label>
            <select
              value={selectedDiagramId}
              onChange={(e) => {
                setSelectedDiagramId(e.target.value);
                setSelectedElementId('');
              }}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground text-sm focus:border-accent focus:outline-none"
            >
              <option value="">Select a diagram...</option>
              {diagrams.map((diagram) => (
                <option key={diagram.id} value={diagram.id}>
                  {diagram.name} ({diagram.elementCount} elements)
                </option>
              ))}
            </select>
          </div>

          {/* Element selector */}
          {selectedDiagramId && (
            <div>
              <label className="block text-sm text-foreground-tertiary mb-1">Element</label>
              <select
                value={selectedElementId}
                onChange={(e) => setSelectedElementId(e.target.value)}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground text-sm focus:border-accent focus:outline-none"
              >
                <option value="">Select an element...</option>
                {elements.map((element) => (
                  <option key={element.id} value={element.id}>
                    {element.name} ({element.type})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Mapping type */}
          <div>
            <label className="block text-sm text-foreground-tertiary mb-1">Mapping Type</label>
            <select
              value={mappingType}
              onChange={(e) => setMappingType(e.target.value as MappingType)}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground text-sm focus:border-accent focus:outline-none"
            >
              {Object.entries(MAPPING_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Sync direction */}
          <div>
            <label className="block text-sm text-foreground-tertiary mb-1">Sync Direction</label>
            <select
              value={syncDirection}
              onChange={(e) => setSyncDirection(e.target.value as SyncDirection)}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground text-sm focus:border-accent focus:outline-none"
            >
              {Object.entries(SYNC_DIRECTION_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 text-foreground-tertiary hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateMapping}
              disabled={creating || !selectedDiagramId || !selectedElementId}
              className="px-4 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? 'Creating...' : 'Create Mapping'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
